import BaseStockProvider from '../BaseStockProvider';

/**
 * Tenor GIF provider (owned by Google)
 * Free API: https://developers.google.com/tenor/guides/quickstart
 * 
 * Tenor API Guidelines:
 * 1. Attribution required - Display "Powered by Tenor" or Tenor logo
 * 2. Must register share events when GIFs are shared
 * 3. Rate limit: Varies by API key tier
 * 4. Free tier available with registration
 */
class TenorProvider extends BaseStockProvider {
  constructor(apiKey = null) {
    super({
      name: 'Tenor',
      apiKey: apiKey || process.env.REACT_APP_TENOR_API_KEY,
      baseUrl: 'https://tenor.googleapis.com/v2',
      mediaTypes: ['gif'],
      requiresAuth: true,
    });
    this.clientKey = 'animegenerator'; // Your app identifier
  }

  /**
   * Search for GIFs on Tenor
   */
  async search({ query, page = 1, perPage = 20 }) {
    if (!this.apiKey) {
      console.warn('Tenor API key not configured');
      return { data: [], hasMore: false, total: 0 };
    }

    try {
      // Tenor uses position-based pagination
      const pos = (page - 1) * perPage;
      
      const response = await fetch(
        `${this.baseUrl}/search?q=${encodeURIComponent(query)}&key=${this.apiKey}&client_key=${this.clientKey}&limit=${perPage}&pos=${pos}&media_filter=gif,tinygif`,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Tenor API error: ${response.status}`);
      }

      const data = await response.json();
      
      // Normalize and filter out invalid formats
      const normalizedItems = (data.results || []).map(item => this.normalizeItem(item, 'gif'));
      const filteredItems = this.filterValidFormats(normalizedItems);
      
      return {
        data: filteredItems,
        hasMore: data.next && data.next !== '0',
        total: null, // Tenor doesn't provide total count
        page: page,
      };
    } catch (error) {
      console.error('Tenor search error:', error);
      return { data: [], hasMore: false, total: 0 };
    }
  }

  /**
   * Get trending/featured GIFs
   * Tenor v2 API uses 'featured' endpoint for trending content
   */
  async getPopular({ page = 1, perPage = 20 }) {
    if (!this.apiKey) {
      console.warn('Tenor API key not configured');
      return { data: [], hasMore: false, total: 0 };
    }

    try {
      const pos = (page - 1) * perPage;
      
      // Try featured endpoint first, fallback to search with empty query for trending
      const response = await fetch(
        `${this.baseUrl}/featured?key=${this.apiKey}&client_key=${this.clientKey}&limit=${perPage}&pos=${pos}&media_filter=gif,tinygif&contentfilter=high`,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        // If featured fails, try search with popular terms as fallback
        console.warn(`Tenor featured endpoint returned ${response.status}, trying search fallback`);
        return await this.search({ query: 'trending', page, perPage });
      }

      const data = await response.json();
      
      // Check if we got results
      if (!data.results || data.results.length === 0) {
        console.warn('Tenor featured returned no results, trying search fallback');
        return await this.search({ query: 'trending', page, perPage });
      }
      
      // Normalize and filter out invalid formats
      const normalizedItems = (data.results || []).map(item => this.normalizeItem(item, 'gif'));
      const filteredItems = this.filterValidFormats(normalizedItems);
      
      return {
        data: filteredItems,
        hasMore: data.next && data.next !== '0',
        total: null,
        page: page,
      };
    } catch (error) {
      console.error('Tenor getPopular error:', error);
      // Fallback to search with popular query
      try {
        return await this.search({ query: 'trending', page, perPage });
      } catch (fallbackError) {
        console.error('Tenor fallback search also failed:', fallbackError);
        return { data: [], hasMore: false, total: 0 };
      }
    }
  }

  /**
   * Register a share event (REQUIRED by Tenor API guidelines)
   * Call this when user selects/uses a GIF
   */
  async registerShare(gifId) {
    if (!this.apiKey || !gifId) {
      return;
    }

    try {
      await fetch(
        `${this.baseUrl}/registershare?id=${gifId}&key=${this.apiKey}&client_key=${this.clientKey}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
    } catch (error) {
      console.error('Tenor share registration error:', error);
    }
  }

  /**
   * Normalize Tenor item to common format
   */
  normalizeItem(item, mediaType) {
    // Access media formats correctly from Tenor API v2
    // media_formats is an object with format types: gif, tinygif, nanogif, etc.
    const gifFormat = item.media_formats?.gif || item.media_formats?.tinygif;
    const previewFormat = item.media_formats?.nanogif || item.media_formats?.tinygif || gifFormat;
    
    return {
      id: `tenor_${item.id}`,
      provider: 'tenor',
      name: item.content_description || item.title || 'GIF',
      thumbnail: previewFormat?.url || gifFormat?.url,
      url: gifFormat?.url,
      downloadUrl: gifFormat?.url,
      type: 'gif',
      category: 'Stock',
      description: item.content_description,
      size: gifFormat?.size ? `${(gifFormat.size / (1024 * 1024)).toFixed(1)} MB` : null,
      width: gifFormat?.dims?.[0],
      height: gifFormat?.dims?.[1],
      author: {
        name: 'Tenor',
        username: 'tenor',
        profileUrl: item.itemurl || 'https://tenor.com',
      },
      sourceUrl: item.itemurl,
      attribution: 'Powered by Tenor',
      attributionUrl: 'https://tenor.com',
      isUserUpload: false,
      // Tenor-specific fields
      tenorId: item.id,
      tags: item.tags || [],
      hasAudio: item.hasaudio || false,
    };
  }

  /**
   * Get attribution text for display
   */
  getAttributionText(item) {
    return 'Powered by Tenor';
  }
}

export default TenorProvider;